import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from 'recharts';
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, TrendingDown, PieChart as PieIcon, Activity } from 'lucide-react';
import { PortfolioSummaryData } from './types';
import { formatCurrency, formatPercentage } from '@/lib/utils'; // Assuming utils exist
import { format } from 'date-fns'; // For date formatting

interface PortfolioPerformanceChartProps {
  summaryData: PortfolioSummaryData | null;
  loading: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-gray-200 shadow-md rounded-md">
        <p className="text-gray-500 text-xs">{label}</p>
        <p className="font-medium">{formatCurrency(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

export const PortfolioPerformanceChart: React.FC<PortfolioPerformanceChartProps> = ({
  summaryData,
  loading,
}) => {

  const renderLoadingState = () => (
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-3/4 mb-1" />
        <Skeleton className="h-4 w-1/2" />
         <div className="flex items-baseline gap-2 pt-2">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-5 w-1/4" />
          </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Skeleton className="h-64 w-full" />
        </div>
        <div className="space-y-4">
           <Skeleton className="h-6 w-1/2 mb-2" />
           <Skeleton className="h-32 w-32 rounded-full mx-auto" />
           <div className="space-y-2 mt-2">
               <Skeleton className="h-4 w-full" />
               <Skeleton className="h-4 w-full" />
               <Skeleton className="h-4 w-3/4" />
           </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderEmptyState = () => (
     <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">Portfolio Performance</CardTitle>
        <CardDescription>Track your portfolio value and allocation.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center justify-center h-64 text-center border border-dashed rounded-lg bg-gray-50">
            <Activity className="h-12 w-12 text-gray-300 mb-3" />
            <h3 className="text-lg font-medium text-gray-500">No Performance Data</h3>
            <p className="text-sm text-gray-400 max-w-xs mt-1">
              Connect your brokerage account or start trading to see your performance history.
            </p>
        </div>
      </CardContent>
     </Card>
  );

  if (loading) {
    return renderLoadingState();
  }

  // Ensure summaryData and its properties exist before accessing them
  if (!summaryData || !summaryData.equityCurve || summaryData.equityCurve.length === 0) {
    return renderEmptyState();
  }

  const {
    totalValue = 0,
    todayChangeValue = 0,
    todayChangePercent = 0,
    equityCurve = [],
    allocation = []
  } = summaryData;

  const isPositiveChange = todayChangePercent >= 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-medium">Portfolio Performance</CardTitle>
        <CardDescription>Overview of portfolio value and asset allocation.</CardDescription>
         <div className="flex items-baseline gap-2 pt-2">
            <span className="text-2xl font-bold">{formatCurrency(totalValue)}</span>
            <span className={`flex items-center text-sm font-medium ${isPositiveChange ? 'text-green-600' : 'text-red-600'}`}>
              {isPositiveChange ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
              {formatCurrency(todayChangeValue)} ({formatPercentage(todayChangePercent)}) Today
            </span>
          </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Equity Curve */}
        <div className="md:col-span-2 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={equityCurve}
              margin={{ top: 5, right: 10, left: -20, bottom: 5 }} // Adjusted margins
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="date"
                tickFormatter={(dateStr) => {
                    try {
                        return format(new Date(dateStr), 'MMM d')
                    } catch {
                        return dateStr; // Fallback if date is invalid
                    }
                 }}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                 tickFormatter={(value) => formatCurrency(value).replace('$','')}
                 tick={{ fontSize: 10, fill: '#6b7280' }}
                 axisLine={false}
                 tickLine={false}
                 domain={['auto', 'auto']}
                 width={50} // Increased width for labels
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={isPositiveChange ? "#10b981" : "#ef4444"}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                isAnimationActive={true}
                 animationDuration={1000}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Allocation Pie Chart */}
        <div className="h-64 flex flex-col items-center">
         <h4 className="text-sm font-medium text-gray-600 mb-2 text-center">Asset Allocation</h4>
         {allocation && allocation.length > 0 ? (
             <>
                <ResponsiveContainer width="100%" height="70%"> {/* Adjusted height */}
                    <PieChart>
                    <Pie
                        data={allocation}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={60} // Adjusted size
                        innerRadius={35} // Doughnut style
                        fill="#8884d8"
                        dataKey="value"
                        paddingAngle={2}
                    >
                        {allocation.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value.toFixed(1)}%`, name]} />
                    </PieChart>
                </ResponsiveContainer>
                {/* Manual Legend */}
                <div className="mt-2 w-full grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    {allocation.map((entry, index) => (
                        <div key={`legend-${index}`} className="flex items-center overflow-hidden">
                            <span className="inline-block w-2 h-2 rounded-full mr-1.5 flex-shrink-0" style={{ backgroundColor: entry.color }}></span>
                            <span className="text-gray-600 truncate" title={entry.name}>{entry.name}</span>
                            <span className="ml-auto font-medium pl-1">{entry.value}%</span>
                        </div>
                    ))}
                </div>
            </>
         ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
                <PieIcon className="h-10 w-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No allocation data available.</p>
            </div>
         )}
        </div>
      </CardContent>
    </Card>
  );
};
